import { FuelPlanResponseStatus } from "enums/fuelPlanResponseStatus";
import {
  FuelInstruction,
  FuelPlanFuelStop,
  FuelPlanResponse,
  FuelPlanStatus,
  FuelPlanStop,
} from "types/fuelPlanResponse";

type DispatchFuelPlanCalculatorDefaults = {
  currentFuelRangeMiles: number | null;
  maxFuelRangeMiles: number | null;
  currentFuelGallons: number | null;
  milesToNextStop: number | null;
  averageMpg: number;
};

type DispatchFuelPlanParams = {
  averageMpg: number;
};

type DispatchFuelPlanFuelStop = {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  distanceFromPreviousStopMiles: number | null;
  milesToNextStop: number | null;
  milesToNextFuelStop?: number | null;
  distanceOffRouteMiles: number | null;
};

type DispatchFuelPlanTimelineItem = {
  type: "LoadStop" | "FuelRecommendation" | "Leg";
  eventSeq: number | null;
  displayName: string | null;
  subtitle: string | null;
  city: string | null;
  state: string | null;
  instruction: string | null;
  instructionLabel: string | null;
  buyGallons: number | null;
  fromEventSeq: number | null;
  miles: number | null;
  milesToNextFuelStop?: number | null;
  isCalculatorDefault: boolean;
  defaultFuelStop: DispatchFuelPlanFuelStop | null;
  fuelStops: DispatchFuelPlanFuelStop[];
};

export type DispatchFuelPlanResponse = {
  generationId?: string | null;
  loadNumber: string;
  status: FuelPlanStatus;
  message: string | null;
  fuelParams: DispatchFuelPlanParams | null;
  calculatorDefaults: DispatchFuelPlanCalculatorDefaults | null;
  timeline: DispatchFuelPlanTimelineItem[];
};

const fuelInstructions: FuelInstruction[] = [
  "FillUp",
  "ChooseNetworkFuelStop",
  "JustEnough",
  "DoNotFuel",
];

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isFuelPlanStatus = (value: unknown): value is FuelPlanStatus =>
  value === FuelPlanResponseStatus.Ok ||
  value === FuelPlanResponseStatus.NotAvailable ||
  value === FuelPlanResponseStatus.Error;

const isFuelInstruction = (value: unknown): value is FuelInstruction =>
  fuelInstructions.includes(value as FuelInstruction);

export const isNormalizedFuelPlanResponse = (
  value: unknown
): value is FuelPlanResponse =>
  isObject(value) &&
  typeof value.loadNumber === "string" &&
  isFuelPlanStatus(value.status) &&
  Array.isArray(value.stops) &&
  (value.calculator === null || isObject(value.calculator));

const isDispatchFuelPlanResponse = (
  value: unknown
): value is DispatchFuelPlanResponse =>
  isObject(value) &&
  typeof value.loadNumber === "string" &&
  isFuelPlanStatus(value.status) &&
  Array.isArray(value.timeline);

const getFuelStops = (
  item: DispatchFuelPlanTimelineItem
): DispatchFuelPlanFuelStop[] => {
  const fuelStops = Array.isArray(item.fuelStops) ? [...item.fuelStops] : [];

  if (
    item.defaultFuelStop &&
    !fuelStops.some(
      (fuelStop) => String(fuelStop.id) === String(item.defaultFuelStop?.id)
    )
  ) {
    fuelStops.push(item.defaultFuelStop);
  }

  return fuelStops;
};

const normalizeFuelStops = (
  item: DispatchFuelPlanTimelineItem
): FuelPlanFuelStop[] => {
  const defaultFuelStopId = item.defaultFuelStop?.id;

  return getFuelStops(item).map((fuelStop) => ({
    id: String(fuelStop.id),
    name: fuelStop.name,
    city: fuelStop.city,
    state: fuelStop.state,
    milesFromPreviousStop: fuelStop.distanceFromPreviousStopMiles,
    milesToNextStop: fuelStop.milesToNextStop,
    milesToNextFuelStop: fuelStop.milesToNextFuelStop ?? null,
    distanceOffRouteMiles: fuelStop.distanceOffRouteMiles,
    isDefault:
      defaultFuelStopId !== undefined &&
      String(fuelStop.id) === String(defaultFuelStopId),
  }));
};

const normalizeStops = (
  timeline: DispatchFuelPlanTimelineItem[]
): FuelPlanStop[] => {
  const legsByFromSequence = new Map<number, DispatchFuelPlanTimelineItem>();

  timeline.forEach((item) => {
    if (item.type === "Leg" && item.fromEventSeq !== null) {
      legsByFromSequence.set(item.fromEventSeq, item);
    }
  });

  return timeline
    .filter(
      (item) => item.type !== "Leg" && item.eventSeq !== null
    )
    .map((item) => {
      const sequence = item.eventSeq as number;
      const nextLeg = legsByFromSequence.get(sequence);
      const isCalculatorEligible =
        item.instruction === "JustEnough";

      return {
        sequence,
        type: item.type as FuelPlanStop["type"],
        displayName: item.displayName ?? "",
        subtitle: item.subtitle,
        city: item.city,
        state: item.state,
        guidance: isFuelInstruction(item.instruction)
          ? {
              code: item.instruction,
              label: item.instructionLabel ?? item.instruction,
              buyGallons: item.buyGallons,
            }
          : null,
        fuelStops: normalizeFuelStops(item),
        nextLeg: nextLeg
          ? {
              miles: nextLeg.miles ?? 0,
              milesToNextFuelStop: nextLeg.milesToNextFuelStop ?? null,
              isCalculatorEligible,
              isCalculatorDefault:
                isCalculatorEligible && nextLeg.isCalculatorDefault,
            }
          : null,
      };
    });
};

export const normalizeFuelPlanResponse = (
  value: unknown
): FuelPlanResponse => {
  if (isNormalizedFuelPlanResponse(value)) {
    return value;
  }

  if (!isDispatchFuelPlanResponse(value)) {
    throw new Error("The fuel plan response was not in the expected format.");
  }

  const calculatorDefaults = value.calculatorDefaults;

  return {
    loadNumber: value.loadNumber,
    generationId: value.generationId,
    status: value.status,
    message: value.message,
    calculator: calculatorDefaults
      ? {
          currentFuelRangeMiles: calculatorDefaults.currentFuelRangeMiles,
          maxFuelRangeMiles: calculatorDefaults.maxFuelRangeMiles ?? null,
          currentFuelGallons: calculatorDefaults.currentFuelGallons,
          averageMpg:
            calculatorDefaults.averageMpg ?? value.fuelParams?.averageMpg ?? 0,
        }
      : null,
    stops: normalizeStops(value.timeline),
  };
};
